/**
 * React Hooks for Supabase Time Periods
 * Provides easy-to-use hooks for fetching and managing time period data
 * Updated for TimePeriod table structure from SQL Server
 */

import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { 
  timePeriodService, 
  SupabaseTimePeriod, 
  TimePeriodFilters, 
  TimePeriodStats 
} from '@/integrations/supabase/periodService';
import { 
  periodNavigationService,
  PeriodNavigationResponse,
  PeriodNavigationItem
} from '@/integrations/supabase/periodNavigationService';
import { usePeriods as useSqlServerPeriods } from '@/hooks/usePeriods';

/**
 * Hook to fetch all time periods with optional filtering
 */
export function useSupabaseTimePeriods(
  filters: TimePeriodFilters = {},
  enabled: boolean = true
): UseQueryResult<SupabaseTimePeriod[], Error> {
  return useQuery({
    queryKey: ['supabase-time-periods', filters],
    queryFn: () => timePeriodService.getTimePeriods(filters),
    enabled,
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });
}

/**
 * Hook to fetch a single time period by ID
 */
export function useSupabaseTimePeriod(
  id: string,
  enabled: boolean = true
): UseQueryResult<SupabaseTimePeriod | null, Error> {
  return useQuery({
    queryKey: ['supabase-time-period', id],
    queryFn: () => timePeriodService.getTimePeriodById(id),
    enabled: enabled && !!id,
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });
}

/**
 * Hook to fetch time periods by type (Weekly, Monthly, Quarterly)
 */
export function useTimePeriodsByType(
  periodType: string,
  year?: number,
  enabled: boolean = true
): UseQueryResult<SupabaseTimePeriod[], Error> {
  return useQuery({
    queryKey: ['time-periods-by-type', periodType, year],
    queryFn: () => {
      const filters: TimePeriodFilters = { period_type: periodType };
      if (year) filters.current_year = year;
      return timePeriodService.getTimePeriods(filters);
    },
    enabled: enabled && !!periodType,
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });
}

/**
 * Hook to fetch time periods by year
 */
export function useTimePeriodsByYear(
  year: number,
  enabled: boolean = true
): UseQueryResult<SupabaseTimePeriod[], Error> {
  return useQuery({
    queryKey: ['time-periods-by-year', year],
    queryFn: () => timePeriodService.getTimePeriodsByYear(year),
    enabled: enabled && !!year,
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });
}

/**
 * Hook to fetch weekly periods
 */
export function useWeeklyTimePeriods(
  year?: number,
  enabled: boolean = true
): UseQueryResult<SupabaseTimePeriod[], Error> {
  return useQuery({
    queryKey: ['weekly-time-periods', year],
    queryFn: () => timePeriodService.getWeeklyPeriods(year),
    enabled,
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });
}

/**
 * Hook to fetch monthly periods
 */
export function useMonthlyTimePeriods(
  year?: number,
  enabled: boolean = true
): UseQueryResult<SupabaseTimePeriod[], Error> {
  return useQuery({
    queryKey: ['monthly-time-periods', year],
    queryFn: () => timePeriodService.getMonthlyPeriods(year),
    enabled,
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });
}

/**
 * Hook to fetch quarterly periods
 */
export function useQuarterlyTimePeriods(
  year?: number,
  enabled: boolean = true
): UseQueryResult<SupabaseTimePeriod[], Error> {
  return useQuery({
    queryKey: ['quarterly-time-periods', year],
    queryFn: () => timePeriodService.getQuarterlyPeriods(year),
    enabled,
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });
}

/**
 * Hook to fetch recent time periods
 */
export function useRecentTimePeriods(
  limit: number = 10,
  enabled: boolean = true
): UseQueryResult<SupabaseTimePeriod[], Error> {
  return useQuery({
    queryKey: ['recent-time-periods', limit],
    queryFn: () => timePeriodService.getRecentTimePeriods(limit),
    enabled,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}

/**
 * Hook to fetch time periods statistics
 */
export function useTimePeriodStats(): UseQueryResult<TimePeriodStats, Error> {
  return useQuery({
    queryKey: ['time-period-stats'],
    queryFn: () => timePeriodService.getTimePeriodStats(),
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });
}

/**
 * Hook to fetch time period options for dropdowns
 */
export function useTimePeriodOptions(): UseQueryResult<Array<{ 
  value: string; 
  label: string; 
  type: string;
  year: number | null;
}>, Error> {
  return useQuery({
    queryKey: ['time-period-options'],
    queryFn: () => timePeriodService.getTimePeriodOptions(),
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });
}

/**
 * Hook to search time periods
 */
export function useSearchTimePeriods(
  searchTerm: string,
  enabled: boolean = true
): UseQueryResult<SupabaseTimePeriod[], Error> {
  return useQuery({
    queryKey: ['search-time-periods', searchTerm],
    queryFn: () => timePeriodService.searchTimePeriods(searchTerm),
    enabled: enabled && searchTerm.trim().length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to get time periods grouped by type
 */
export function useTimePeriodsGroupedByType(): UseQueryResult<Record<string, SupabaseTimePeriod[]>, Error> {
  return useQuery({
    queryKey: ['time-periods-grouped-by-type'],
    queryFn: () => timePeriodService.getTimePeriodsGroupedByType(),
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });
}

/**
 * Hook to get time periods grouped by year
 */
export function useTimePeriodsGroupedByYear(): UseQueryResult<Record<number, SupabaseTimePeriod[]>, Error> {
  return useQuery({
    queryKey: ['time-periods-grouped-by-year'],
    queryFn: () => timePeriodService.getTimePeriodsGroupedByYear(),
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });
}

/**
 * Hook for current time period
 */
export function useCurrentTimePeriod(): UseQueryResult<SupabaseTimePeriod | null, Error> {
  return useQuery({
    queryKey: ['current-time-period'],
    queryFn: () => timePeriodService.getCurrentTimePeriod(),
    staleTime: 15 * 60 * 1000, // 15 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}

/**
 * Hook to test sync functionality
 */
export function useTimePeriodSyncTest(): UseQueryResult<{ available: boolean; message: string }, Error> {
  return useQuery({
    queryKey: ['time-period-sync-test'],
    queryFn: () => timePeriodService.testSync(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    enabled: false, // Only run when manually triggered
  });
}

/**
 * Utility hook for formatting
 */
export function useTimePeriodFormatter() {
  return {
    formatTimePeriodDisplay: timePeriodService.formatTimePeriodDisplay,
  };
}

/**
 * Hook for type-specific queries with better performance
 */
export function useTimePeriodsByTypes(
  types: string[],
  year?: number,
  enabled: boolean = true
): UseQueryResult<Record<string, SupabaseTimePeriod[]>, Error> {
  return useQuery({
    queryKey: ['time-periods-by-types', types, year],
    queryFn: async () => {
      const results: Record<string, SupabaseTimePeriod[]> = {};
      
      for (const type of types) {
        const filters: TimePeriodFilters = { period_type: type };
        if (year) filters.current_year = year;
        results[type] = await timePeriodService.getTimePeriods(filters);
      }
      
      return results;
    },
    enabled: enabled && types.length > 0,
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });
}

/**
 * Hook for date range queries
 */
export function useTimePeriodsByDateRange(
  startDate: string,
  endDate: string,
  enabled: boolean = true
): UseQueryResult<SupabaseTimePeriod[], Error> {
  return useQuery({
    queryKey: ['time-periods-by-date-range', startDate, endDate],
    queryFn: () => timePeriodService.getTimePeriods({
      end_date_from: startDate,
      end_date_to: endDate
    }),
    enabled: enabled && !!startDate && !!endDate,
    staleTime: 15 * 60 * 1000, // 15 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}

/**
 * Default export with commonly used hooks
 */
export default {
  useSupabaseTimePeriods,
  useSupabaseTimePeriod,
  useTimePeriodsByType,
  useTimePeriodsByYear,
  useWeeklyTimePeriods,
  useMonthlyTimePeriods,
  useQuarterlyTimePeriods,
  useRecentTimePeriods,
  useTimePeriodStats,
  useTimePeriodOptions,
  useSearchTimePeriods,
  useTimePeriodsGroupedByType,
  useTimePeriodsGroupedByYear,
  useCurrentTimePeriod,
  useTimePeriodSyncTest,
  useTimePeriodFormatter,
  useTimePeriodsByTypes,
  useTimePeriodsByDateRange,
};

/**
 * ========================================
 * PERIOD NAVIGATION HOOKS (NEW - using TimePeriods table)
 * Replaces usePeriods hook with Supabase-based implementation
 * ========================================
 */

/**
 * Hook to fetch periods for navigation using TimePeriods table
 * Replaces usePeriods hook with Supabase-based implementation
 * ✅ Proper pagination: Z-A sorting, 4 per page, unlimited pages
 * Falls back to SQL Server if needed
 */
export function useSupabasePeriodNavigation(
  page: number = 1,
  limit: number = 4,
  enabled: boolean = true
): UseQueryResult<PeriodNavigationResponse, Error> {
  // Try Supabase TimePeriods first with proper pagination
  const supabaseQuery = useQuery({
    queryKey: ['supabase-period-navigation', page, limit],
    queryFn: () => periodNavigationService.getPeriods(page, limit),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
    retry: 1, // Only retry once, then fallback
    // Don't refetch on window focus to avoid unnecessary requests
    refetchOnWindowFocus: false,
    // Keep previous data during pagination
    placeholderData: (previousData) => previousData,
  });

  // Fallback to SQL Server if Supabase fails (but not if just no data on current page)
  const shouldFallback = enabled && supabaseQuery.isError && !supabaseQuery.data;
  const sqlServerQuery = useSqlServerPeriods(
    { limit }, 
    shouldFallback
  );

  // Return appropriate response
  if (supabaseQuery.data) {
    // Supabase has data (or attempted to fetch), use it
    return supabaseQuery;
  } else if (sqlServerQuery.periods?.length) {
    // Fallback to SQL Server data only if Supabase completely failed
    return {
      ...supabaseQuery,
      data: {
        data: sqlServerQuery.periods,
        pagination: sqlServerQuery.pagination || {
          page: 1,
          limit,
          offset: 0,
          totalCount: sqlServerQuery.periods.length,
          totalPages: 1,
          hasNextPage: false,
          hasPrevPage: false,
        },
      },
      isLoading: sqlServerQuery.loading,
      error: sqlServerQuery.error ? new Error(sqlServerQuery.error) : null,
      isError: !!sqlServerQuery.error,
    } as UseQueryResult<PeriodNavigationResponse, Error>;
  } else {
    // Return Supabase query (even if empty - this allows for empty pages)
    return supabaseQuery;
  }
}

/**
 * Hook for period navigation statistics from TimePeriods
 */
export function usePeriodNavigationStats(enabled: boolean = true) {
  return useQuery({
    queryKey: ['period-navigation-stats'],
    queryFn: () => periodNavigationService.getPeriodStats(),
    enabled,
    staleTime: 15 * 60 * 1000, // 15 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}

/**
 * Hook to get all available periods for dropdowns
 */
export function useAllPeriodsForNavigation(enabled: boolean = true) {
  return useQuery({
    queryKey: ['all-periods-navigation'],
    queryFn: () => periodNavigationService.getAllPeriods(),
    enabled,
    staleTime: 30 * 60 * 1000, // 30 minutes - period list rarely changes
    gcTime: 60 * 60 * 1000, // 1 hour
  });
}

/**
 * Hook to check if a specific period exists
 */
export function usePeriodExists(periodId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: ['period-exists', periodId],
    queryFn: () => periodNavigationService.periodExists(periodId),
    enabled: enabled && !!periodId,
    staleTime: 60 * 60 * 1000, // 1 hour - existence rarely changes
    gcTime: 2 * 60 * 60 * 1000, // 2 hours
  });
}

/**
 * Legacy hook that mimics original usePeriods interface
 * Provides backward compatibility during migration
 * 🔄 REPLACEMENT FOR usePeriods HOOK
 */
export function usePeriodsFromSupabase(
  options: { limit?: number } = {},
  page: number = 1
) {
  const { limit = 4 } = options;
  
  const { 
    data: periodsResponse, 
    isLoading: loading, 
    error, 
    refetch 
  } = useSupabasePeriodNavigation(page, limit);

  return {
    periods: periodsResponse?.data || [],
    pagination: periodsResponse?.pagination || null,
    loading,
    error: error?.message || null,
    refresh: refetch,
    goToPage: (newPage: number) => {
      // This would require state management for pagination
      console.log(`Navigate to page ${newPage}`);
      return refetch();
    },
    nextPage: () => {
      console.log('Navigate to next page');
      return refetch();
    },
    prevPage: () => {
      console.log('Navigate to previous page');
      return refetch();
    },
  };
}

// Backward compatibility aliases
export const useSupabasePeriods = useSupabaseTimePeriods;
export const useSupabasePeriod = useSupabaseTimePeriod;
export const usePeriodsByYear = useTimePeriodsByYear;
export const useRecentPeriods = useRecentTimePeriods;
export const usePeriodsStats = useTimePeriodStats;
export const usePeriodOptions = useTimePeriodOptions;
export const useSearchPeriods = useSearchTimePeriods;

// New navigation aliases
export const usePeriodsNavigation = useSupabasePeriodNavigation;
export const usePeriodsNavigationStats = usePeriodNavigationStats;
export const useAllPeriods = useAllPeriodsForNavigation;